// Client class for API communication and streaming

import { Thread, Message, StreamChunk } from "../types/sdk";
import {
  toAnthropicFormat,
  isStoragePdfBlock,
  isStorageImageBlock,
  storagePdfBlockToAnthropicFormat,
  storageImageBlockToAnthropicFormat,
} from "@/lib/storage";
import { convertFirebaseMessages, type FirebaseMessage } from "./message-converter";
import type { Base64ContentBlock } from "../types/messages";
import type { ClientConfig } from "@/types/api";
import { getDb } from "@/lib/firebase";
import { fetch as apiFetch } from "@/lib/api";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import {
  USER_ID_KEY,
  API_ENDPOINTS,
  FIRESTORE_COLLECTIONS,
  createCitationMarker,
} from "@/constants";
import { getIdentifierConfig } from "@/lib/identifier-config";
import { type AddressData } from "@/services/conversation-service";

/**
 * Generate timestamp-based message ID for chronological sorting in Firebase.
 * Format: {timestamp_ms}_{random_suffix}_{type}
 * Example: 1702399847523_a7f3e2_assistant
 */
function generateMessageId(type: "user" | "assistant" | "tool"): string {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  return `${timestamp}_${randomSuffix}_${type}`;
}

/**
 * Get user ID from sessionStorage or use provided value
 */
function getUserId(fallback?: string): string {
  return sessionStorage.getItem(USER_ID_KEY) || fallback || "anonymous";
}

export class Client {
  private apiUrl: string;
  private userId: string;
  public threads: {
    search: (params: {
      metadata?: Record<string, any>;
      limit?: number;
    }) => Promise<Thread[]>;
  };

  constructor(config: ClientConfig) {
    // VITE_BACKEND_URL is the single source of truth for backend API URL
    this.apiUrl = import.meta.env.VITE_BACKEND_URL;
    this.userId = config.userId || getUserId();

    // Create threads namespace
    this.threads = {
      search: async (params) => {
        const response = await this.fetch(API_ENDPOINTS.threadSearch(), {
          method: "POST",
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          throw new Error(`Failed to search threads: ${response.statusText}`);
        }

        const data = await response.json();
        return data.threads || [];
      },
    };
  }

  private async fetch(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const url = `${this.apiUrl}${endpoint}`;

    // Use centralized fetch wrapper (handles App Check, Content-Type)
    return apiFetch(url, {
      ...options,
      userId: this.userId,
      headers: {
        ...((options.headers as Record<string, string>) || {}),
      },
    });
  }

  async *createThread(params: {
    participant_uid: string;
    userName?: string;
    language: string;
    conversationName?: string;
    projectId?: string;
    address?: AddressData | null;
  }): AsyncGenerator<{ threadId: string; chunk?: StreamChunk }, void, unknown> {
    const response = await this.fetch(API_ENDPOINTS.CREATE_THREAD, {
      method: "POST",
      body: JSON.stringify({
        user_name: params.userName || "",
        language: params.language,
        project_name: params.conversationName,
        project_id: params.projectId,
        address: params.address?.formattedAddress || null,
        address_lat: params.address?.lat || null,
        address_lng: params.address?.lng || null,
        address_place_id: params.address?.placeId || null,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create thread: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let threadId = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter((line) => line.trim());

        for (const line of lines) {
          try {
            const parsed = JSON.parse(line);

            // First event should be thread_created with thread_id
            if (parsed.type === "thread_created") {
              threadId = parsed.thread_id;
              yield { threadId };
            } else {
              // Subsequent events are welcome message streaming
              yield {
                threadId,
                chunk: {
                  event: "values",
                  data: parsed,
                },
              };
            }
          } catch (e) {
            console.warn("Failed to parse chunk:", line, e);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async getThread(threadId: string): Promise<Thread> {
    const response = await this.fetch(API_ENDPOINTS.threadDetail(threadId));

    if (!response.ok) {
      throw new Error(`Failed to get thread: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteThread(threadId: string): Promise<void> {
    const response = await this.fetch(API_ENDPOINTS.thread(threadId), {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to delete thread: ${response.statusText}`);
    }
  }

  async getConversationHistory(threadId: string): Promise<Message[]> {
    // Query Firebase directly instead of calling backend API
    // This avoids race conditions and 404 errors when thread is just created
    try {
      const db = getDb();
      const config = getIdentifierConfig();
      const messagesRef = collection(
        db,
        FIRESTORE_COLLECTIONS.USERS,
        this.userId,
        config.collectionName, // Use dynamic collection name from config
        threadId,
        FIRESTORE_COLLECTIONS.MESSAGES,
      );
      const q = query(messagesRef, orderBy("created_at", "asc"));
      const snapshot = await getDocs(q);

      // Convert Firebase documents to message objects
      const firebaseMessages: FirebaseMessage[] = [];
      snapshot.forEach((doc) => {
        firebaseMessages.push(doc.data() as FirebaseMessage);
      });

      // Convert Firebase messages to frontend format using dedicated converter
      return convertFirebaseMessages(firebaseMessages);
    } catch (error) {
      console.error("Failed to load messages from Firebase:", error);
      // Return empty array instead of throwing - thread might be brand new
      return [];
    }
  }

  async abortAgent(threadId: string): Promise<void> {
    const response = await this.fetch(`/llm/projects/${threadId}/agent/abort`, {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`Failed to abort agent: ${response.statusText}`);
    }
  }

  async *stream(
    threadId: string | null,
    input: any,
    _config?: Record<string, any>,
  ): AsyncGenerator<StreamChunk> {
    if (!threadId) {
      throw new Error("Thread ID required for streaming");
    }

    // Use agent endpoint for full tool support (recommended)
    const endpoint = API_ENDPOINTS.threadStream(threadId);

    // Extract message content and ID from input (keep full multimodal content)
    let messageContent: string | any[];
    let userMessageId: string | undefined;
    if (
      input.messages &&
      Array.isArray(input.messages) &&
      input.messages.length > 0
    ) {
      const lastMessage = input.messages[input.messages.length - 1];
      userMessageId = lastMessage.id; // Extract message ID

      // Convert content from internal format to Anthropic API format
      if (Array.isArray(lastMessage.content)) {
        messageContent = lastMessage.content.map((block: any) => {
          // Text blocks pass through
          if (block.type === "text") {
            return block;
          }
          // Convert storage PDF blocks to Anthropic format (backend will resolve)
          if (isStoragePdfBlock(block)) {
            return storagePdfBlockToAnthropicFormat(block);
          }
          // Convert storage image blocks to Anthropic format (backend will resolve)
          if (isStorageImageBlock(block)) {
            return storageImageBlockToAnthropicFormat(block);
          }
          // Convert Base64ContentBlock to Anthropic format
          if (block.source_type === "base64") {
            return toAnthropicFormat(block as Base64ContentBlock);
          }
          // Other blocks pass through
          return block;
        });
      } else {
        messageContent = lastMessage.content || "";
      }
    } else {
      // Empty messages array - treat as welcome message trigger
      messageContent = "";
    }

    // Extract tool_results if present (for human-in-the-loop approval/edit/reject)
    const toolResults = input.tool_results;

    // Build request body
    const requestBody: any = {};

    // Add messages array if present (backend expects 'messages' not 'message')
    // Include message ID from frontend for consistent optimistic updates
    if (messageContent && userMessageId) {
      requestBody.messages = [{ id: userMessageId, content: messageContent }];
    }

    // Add tool_results if present
    if (toolResults) {
      requestBody.tool_results = toolResults;
    }

    const response = await this.fetch(endpoint, {
      method: "POST",
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body available");
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let fullContent = "";
    let citations: any[] = [];
    let statusHistory: any[] = [];
    let messageId = generateMessageId("assistant");
    let thinkingContent = "";


    // Tool input accumulators: one per tool_use_id for streaming partial JSON
    const toolInputAccumulators = new Map<
      string,
      {
        accumulatedJson: string;
        extractedFields: Record<string, any>;
        toolName: string;
        toolId: string;
        humanInTheLoop: boolean;
      }
    >();

    // Track content blocks with insertion order for chronological rendering
    // Each entry: { type: "thinking" | "text" | "tool_use", insertionIndex: number, data: any }
    const orderedContentBlocks: Array<{
      type: string;
      insertionIndex: number;
      data: any;
    }> = [];
    let insertionCounter = 0;
    let textProcessedUpToLastTool = ""; // Stores fullContent snapshot at last tool_use - used to find new text between tools
    let lastYieldedContentLength = 0; // Track content length at last yield to avoid duplicate final yields

    // PERSISTENT state: Track completed tool IDs across message chunks
    // This survives the orderedContentBlocks reset and prevents tools from
    // flickering back to loading state when a new tool starts
    const completedToolIds = new Set<string>();

    // Helper: Build content array in chronological order
    const buildContentArray = (): any[] => {
      // Clone ordered blocks
      const blocks = [...orderedContentBlocks];

      // If we have text content that came AFTER the last tool_use, add it
      if (fullContent.length > textProcessedUpToLastTool.length) {
        const textAfterTools = fullContent.substring(textProcessedUpToLastTool.length);
        if (textAfterTools.trim()) {
          blocks.push({
            type: "text",
            insertionIndex: insertionCounter, // Current position (after all tools)
            data: { type: "text", text: textAfterTools },
          });
        }
      }

      // Sort by insertion index to maintain chronological order
      const sorted = blocks.sort((a, b) => a.insertionIndex - b.insertionIndex);

      // Merge adjacent text blocks AND apply persistent is_completed state
      const merged: any[] = [];
      for (const block of sorted) {
        if (block.data.type === "text" && merged.length > 0 && merged[merged.length - 1].type === "text") {
          // Merge with previous text block
          merged[merged.length - 1].text += block.data.text;
        } else if (block.data.type === "tool_use") {
          // Apply persistent is_completed state from completedToolIds
          // This ensures tools don't flicker back to loading when content resets
          merged.push({
            ...block.data,
            is_completed: block.data.is_completed || completedToolIds.has(block.data.id),
          });
        } else {
          merged.push(block.data);
        }
      }

      return merged;
    };


    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const rawChunk = decoder.decode(value, { stream: true });
        buffer += rawChunk;
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            // Parse JSON Line (NDJSON format)
            const event = JSON.parse(line);

            if (event.type === "text") {
              // ═══════════════════════════════════════════════════════════════════
              // TEXT TOKEN HANDLING - ALWAYS YIELD
              // ═══════════════════════════════════════════════════════════════════
              // We ALWAYS yield for every text event to ensure tokens are rendered
              // incrementally during both normal streaming AND replay scenarios.
              //
              // During replay (page refresh), all buffered events arrive in a single
              // synchronous batch. We must yield for each one to trigger React
              // state updates, otherwise messages only appear when stream completes.
              // ═══════════════════════════════════════════════════════════════════
              fullContent += event.content;

              // Yield updated message state for every text token
              const contentArray = buildContentArray();
              yield {
                event: "values",
                data: {
                  messages: [
                    {
                      id: messageId,
                      type: "ai" as const,
                      role: "assistant",
                      content: contentArray,
                      citations: citations,
                      status_history: statusHistory,
                    },
                  ],
                },
              };
              // Track that we've yielded this content state
              lastYieldedContentLength = fullContent.length + orderedContentBlocks.length;
              continue;
            } else if (event.type === "thinking") {
              // ═══════════════════════════════════════════════════════════════════
              // THINKING BLOCK DATA FLOW - STEP 1: Event Reception
              // ═══════════════════════════════════════════════════════════════════
              // Backend streams: {type: "thinking", content: "...", is_loading: bool}
              // First event has is_loading: true, content: "" (empty)
              // Subsequent events have is_loading: false, content: "token..."
              //
              // CRITICAL: We MUST yield a message with content array containing
              // a thinking block, even when content is empty. This ensures:
              // 1. useThreadMessages.ts -> shouldHideMessage() returns false
              //    (checks hasThinkingBlocks in content array)
              // 2. AIMessage.tsx -> shouldHideEmptyMessage is false
              //    (checks hasThinkingBlocks before early return)
              // 3. ThinkingBlock component renders with "Claude is thinking" header
              // ═══════════════════════════════════════════════════════════════════

              // Accumulate thinking content across events
              thinkingContent += event.content || "";

              // Update or create thinking block in ordered list
              const thinkingBlockIndex = orderedContentBlocks.findIndex(b => b.type === "thinking");
              const thinkingBlock = {
                type: "thinking",
                thinking: thinkingContent,
              };

              if (thinkingBlockIndex >= 0) {
                // Update existing thinking block
                orderedContentBlocks[thinkingBlockIndex].data = thinkingBlock;
              } else {
                // First thinking block - always comes first (insertion index 0)
                orderedContentBlocks.push({
                  type: "thinking",
                  insertionIndex: 0,
                  data: thinkingBlock,
                });
                // Reserve index 1+ for content that follows
                insertionCounter = 1;
              }

              // Yield as "values" event -> useStreamWithBackend.ts will update state
              const messageWithThinking = {
                id: messageId,
                type: "ai" as const,
                role: "assistant",
                content: buildContentArray(),
                citations: citations,
                status_history: statusHistory,
              };

              yield {
                event: "values",
                data: { messages: [messageWithThinking] },
              };
              // Track that we've yielded this content state
              lastYieldedContentLength = fullContent.length + orderedContentBlocks.length;

              continue;
            } else if (event.type === "citation") {
              // Backend buffers citations and sends them AFTER all text for a content block
              // We must insert the marker at the current position in fullContent
              citations.push(event.citation);
              const citationNumber = citations.length;
              fullContent += createCitationMarker(citationNumber);
            } else if (event.type === "tool_input") {
              // ═══════════════════════════════════════════════════════════════════
              // TWO-ROLE MODEL: Tool inputs as content blocks in AI message
              // ═══════════════════════════════════════════════════════════════════
              // In the two-role model, tool_use blocks are embedded in the AI message
              // content array, not as separate messages. This ensures:
              // 1. Proper rendering via AIMessage → ToolInput component
              // 2. Correct message ordering during page reload
              // 3. HITL metadata stored directly in tool_use blocks
              //
              // Data flow: event → toolUseBlocks array → AI message content
              //            → AIMessage (hasToolUseBlocks) → ToolInput.tsx
              // ═══════════════════════════════════════════════════════════════════
              const toolUseId = event.tool_use_id;

              if (!toolInputAccumulators.has(toolUseId)) {
                toolInputAccumulators.set(toolUseId, {
                  accumulatedJson: "",
                  extractedFields: {},
                  toolName: event.tool_name,
                  toolId: toolUseId,
                  humanInTheLoop: event.human_in_the_loop || false,
                });
              }

              const accumulator = toolInputAccumulators.get(toolUseId)!;

              if (event.input_snapshot?.partial_json) {
                accumulator.accumulatedJson +=
                  event.input_snapshot.partial_json;

                // Try to parse the partial JSON dynamically
                try {
                  // Attempt to parse as-is (works if JSON is complete enough)
                  accumulator.extractedFields = JSON.parse(
                    accumulator.accumulatedJson,
                  );
                } catch {
                  // JSON is incomplete - try to close open strings and objects
                  try {
                    const fixedJson = accumulator.accumulatedJson + '"}';
                    accumulator.extractedFields = JSON.parse(fixedJson);
                  } catch {
                    // Still can't parse - keep previous extracted fields
                  }
                }
              }

              // Create or update tool_use block data
              const toolUseBlock = {
                type: "tool_use",
                id: toolUseId,
                name: event.tool_name,
                input: accumulator.extractedFields || event.input_snapshot || {},
                // HITL fields (custom, not sent to Anthropic API)
                requires_approval: event.human_in_the_loop || false,
                approval_status: event.human_in_the_loop ? "pending" : undefined,
              };

              // Update or add tool_use block in ordered list (preserves insertion position)
              const toolBlockIndex = orderedContentBlocks.findIndex(
                (b) => b.type === "tool_use" && b.data.id === toolUseId
              );

              if (toolBlockIndex >= 0) {
                // Update existing tool_use block
                orderedContentBlocks[toolBlockIndex].data = toolUseBlock;
              } else {
                // First time seeing this tool_use - capture text content BEFORE it
                const textBeforeTool = fullContent.substring(textProcessedUpToLastTool.length);

                // Add text block for content that came BEFORE this tool
                if (textBeforeTool.trim()) {
                  orderedContentBlocks.push({
                    type: "text",
                    insertionIndex: insertionCounter++,
                    data: { type: "text", text: textBeforeTool },
                  });
                }

                // Add tool_use block at current insertion point
                orderedContentBlocks.push({
                  type: "tool_use",
                  insertionIndex: insertionCounter++,
                  data: toolUseBlock,
                });

                // Update marker: all text up to here has been placed
                textProcessedUpToLastTool = fullContent;
              }

              // Yield updated AI message with content in chronological order
              const messageWithToolUse = {
                id: messageId,
                type: "ai" as const,
                role: "assistant",
                content: buildContentArray(),
                citations: citations,
                status_history: statusHistory,
              };

              yield {
                event: "values",
                data: { messages: [messageWithToolUse] },
              };
              // Track that we've yielded this content state
              lastYieldedContentLength = fullContent.length + orderedContentBlocks.length;

              // Clear accumulator when tool input is complete
              if (event.is_complete) {
                toolInputAccumulators.delete(toolUseId);
              }

              continue; // Skip normal AI message yield
            } else if (event.type === "tool_result") {
              // ═══════════════════════════════════════════════════════════════════
              // TWO-ROLE MODEL: Tool results as content blocks in user message
              // ═══════════════════════════════════════════════════════════════════
              // In the two-role model, tool_result blocks are embedded in a user
              // message content array. When tool_result arrives, we:
              // 1. Finalize current AI message (thinking + text + tool_use)
              // 2. Create user message with tool_result content block
              // 3. Reset accumulators for next AI message chunk
              //
              // Data flow: event → user message with tool_result content
              //            → HumanMessage (hasToolResultBlocks) → ToolResult.tsx
              // ═══════════════════════════════════════════════════════════════════

              // Mark the tool as completed in BOTH places:
              // 1. orderedContentBlocks (for current message chunk)
              // 2. completedToolIds (PERSISTENT, survives reset for future chunks)
              const toolBlockIndex = orderedContentBlocks.findIndex(
                (b) => b.type === "tool_use" && b.data.id === event.tool_use_id
              );
              if (toolBlockIndex >= 0) {
                orderedContentBlocks[toolBlockIndex].data.is_completed = true;
              }
              // Add to persistent set - this prevents flickering when orderedContentBlocks resets
              completedToolIds.add(event.tool_use_id);

              // 1. Finalize current AI message if it has any content
              if (fullContent.trim().length > 0 || thinkingContent.trim().length > 0 || orderedContentBlocks.length > 0) {
                // buildContentArray() will handle remaining text after last tool_use
                const finalAIMessage = {
                  id: messageId,
                  type: "ai" as const,
                  role: "assistant",
                  content: buildContentArray(),
                  citations: citations,
                  status_history: statusHistory,
                };

                yield {
                  event: "values",
                  data: { messages: [finalAIMessage] },
                };
              }

              // 2. Create user message with tool_result content block (two-role model)
              const toolResultUserMessage = {
                id: `${Date.now()}_tool_result_${event.tool_use_id}`,
                type: "human" as const,
                role: "user",
                content: [
                  {
                    type: "tool_result",
                    tool_use_id: event.tool_use_id,
                    tool_name: event.tool_name, // Keep for frontend display
                    content: event.output,
                    is_error: event.is_error || false,
                  },
                ],
              };

              yield {
                event: "values",
                data: { messages: [toolResultUserMessage] },
              };

              // 3. Reset for next AI message chunk
              const newMessageId = generateMessageId("assistant");
              messageId = newMessageId;
              fullContent = "";
              thinkingContent = "";
              citations = [];
              statusHistory = [];
              orderedContentBlocks.length = 0; // Clear ordered content blocks for next cycle
              insertionCounter = 0; // Reset insertion counter
              textProcessedUpToLastTool = ""; // Reset text tracking
              lastYieldedContentLength = 0; // Reset yield tracking for new message
              completedToolIds.clear(); // Clear completed tool IDs for new message cycle

              continue; // Skip normal AI message yield
            } else if (event.type === "status") {
              // IMMEDIATELY yield status_change event for loader updates
              // This bypasses message batching and allows immediate UI updates
              yield {
                event: "status_change",
                data: { status: event.status },
              };

              // Add status to history array for message tracking
              const statusEvent = {
                status: event.status,
                message: event.message,
                metadata: event.metadata || {},
              };

              statusHistory.push(statusEvent);

              // Also yield updated message for status_history tracking
              if (event.status === "message_received" || event.status === "calling_llm") {
                const statusBlocks: any[] = [];

                if (thinkingContent.trim()) {
                  statusBlocks.push({
                    type: "thinking",
                    thinking: thinkingContent,
                  });
                }

                if (fullContent.trim()) {
                  statusBlocks.push({
                    type: "text",
                    text: fullContent,
                  });
                }

                const messageWithStatus = {
                  id: messageId,
                  type: "ai" as const,
                  role: "assistant",
                  content: statusBlocks.length > 0 ? statusBlocks : fullContent,
                  citations: citations,
                  status_history: statusHistory,
                };

                yield {
                  event: "values",
                  data: { messages: [messageWithStatus] },
                };
              }
              // Yield updated message immediately for RESPONSE_STARTED (so loader can hide)
              else if (event.status === "response_started") {
                // Build content array with thinking + text
                const statusBlocks: any[] = [];

                if (thinkingContent.trim()) {
                  statusBlocks.push({
                    type: "thinking",
                    thinking: thinkingContent,
                  });
                }

                if (fullContent.trim()) {
                  statusBlocks.push({
                    type: "text",
                    text: fullContent,
                  });
                }

                const messageWithStatus = {
                  id: messageId,
                  type: "ai" as const,
                  role: "assistant",
                  content: statusBlocks.length > 0 ? statusBlocks : fullContent,
                  citations: citations,
                  status_history: statusHistory,
                };

                yield {
                  event: "values",
                  data: { messages: [messageWithStatus] },
                };
              }
              // RESPONSE_COMPLETE: Don't yield here - let the stream-end handler do it
              // This prevents the "instant overwrite" of content that was still being
              // animated by TypewriterText. All content flows through the normal
              // token-by-token path and TypewriterText completes its animation.
              else if (event.status === "response_complete") {
                // Reset statusHistory for next message iteration
                statusHistory = [];
                // Don't yield - stream-end finalization will handle the final message
              }

              // Skip normal AI message yield for all status events
              continue;
            } else if (event.type === "message_start") {
              // Metadata event: message started with ID and input token count
              // Use backend's message_id for consistent identification across reconnections
              if (event.message_id) {
                messageId = event.message_id;
              }
              continue; // Don't yield as message
            } else if (event.type === "message_delta") {
              // Metadata event: contains stop_reason and output token count
              continue; // Don't yield as message
            } else if (event.type === "assistant_message") {
              // Metadata event: assistant message complete (already handled by normal flow)
              continue; // Don't yield as message
            } else if (event.type === "aborted") {
              // Stream was aborted by user - yield the aborted event
              // Frontend will stop the stream when it receives this
              console.log("[Client] Stream aborted by user");

              // Yield final AI message with accumulated content before signaling abort
              const abortedBlocks: any[] = [];

              if (thinkingContent.trim()) {
                abortedBlocks.push({
                  type: "thinking",
                  thinking: thinkingContent,
                });
              }

              if (fullContent.trim()) {
                abortedBlocks.push({
                  type: "text",
                  text: fullContent,
                });
              }

              if (abortedBlocks.length > 0) {
                const abortedAIMessage = {
                  id: messageId,
                  type: "ai" as const,
                  role: "assistant",
                  content: abortedBlocks,
                  citations: citations,
                  status_history: statusHistory,
                };

                yield {
                  event: "values",
                  data: { messages: [abortedAIMessage] },
                };
              }

              // Yield the aborted event to signal frontend to stop
              yield {
                event: "aborted",
                data: {},
              };
              return; // Exit the generator
            } else {
              console.warn("⚠️ Unknown event type:", event.type, event);
            }

            // Yield in the format expected by useStreamWithBackend
            // For text and citation events
            // Build content array if we have thinking content
            const finalContentBlocks: any[] = [];

            // Add thinking block if we accumulated thinking content
            if (thinkingContent.trim()) {
              finalContentBlocks.push({
                type: "thinking",
                thinking: thinkingContent,
              });
            }

            // Add text block if we have text content
            // NOTE: Do NOT include citations in text block - markers are already in fullContent
            // Citations array is only at message level for tooltip lookups
            if (fullContent.trim()) {
              finalContentBlocks.push({
                type: "text",
                text: fullContent,
              });
            }

            const messageData = {
              id: messageId,
              type: "ai" as const,
              role: "assistant",
              content: finalContentBlocks.length > 0 ? finalContentBlocks : fullContent,
              citations: citations,
              status_history: statusHistory,
            };

            yield {
              event: "values",
              data: {
                messages: [messageData],
              },
            };
          } catch (e) {
            console.error("Failed to parse NDJSON:", e);
          }
        }
      }

      // Yield final message state ONLY if there's new content since last yield
      // This prevents the "instant overwrite" that disrupts TypewriterText animation
      const currentContentLength = fullContent.length + orderedContentBlocks.length;
      if (currentContentLength > lastYieldedContentLength) {
        const finalContent = buildContentArray();
        if (finalContent.length > 0) {
          yield {
            event: "values",
            data: {
              messages: [
                {
                  id: messageId,
                  type: "ai" as const,
                  role: "assistant",
                  content: finalContent,
                  citations: citations,
                  status_history: statusHistory,
                },
              ],
            },
          };
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async searchThreads(metadata?: Record<string, any>): Promise<Thread[]> {
    const response = await this.fetch(API_ENDPOINTS.threadSearch(), {
      method: "POST",
      body: JSON.stringify({ metadata }),
    });

    if (!response.ok) {
      throw new Error(`Failed to search threads: ${response.statusText}`);
    }

    const data = await response.json();
    return data.threads || [];
  }
}

export function createClient(userId: string): Client {
  return new Client({ userId });
}
